import { useEffect, useState } from "react";
import { api, type BusinessType } from "../../../../lib/api";

export function DescriptionSuggestions({
  type, name, tagline, onPick,
}: {
  type: BusinessType;
  name: string;
  tagline: string;
  onPick: (text: string) => void;
}) {
  type Phase = "hidden" | "thinking" | "ready" | "error";
  const [phase, setPhase] = useState<Phase>("hidden");
  const [chips, setChips] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const trimmedName = name.trim();
  const trimmedTagline = tagline.trim();
  const eligible = !!type && trimmedName.length >= 3;

  // Debounced AI fetch. We wait 1.5s after typing settles before calling
  // the endpoint. 700ms re-fired on every pause in a slowly typed name and
  // tripped the API's rate limit mid-wizard (staging, 2026-09-06: 15 calls
  // in 35s, the last twelve 429s).
  useEffect(() => {
    if (!eligible) {
      setPhase("hidden");
      setChips([]);
      setErrMsg(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      setPhase("thinking");
      setErrMsg(null);
      api.draftDescription({
        businessName: trimmedName,
        tagline: trimmedTagline || undefined,
        type,
      })
        .then((res) => {
          if (cancelled) return;
          if (!res.suggestions?.length) {
            setPhase("error");
            setErrMsg("Couldn't draft starters. Describe in your own words below.");
            return;
          }
          setChips(res.suggestions);
          setPhase("ready");
        })
        .catch((err: any) => {
          if (cancelled) return;
          setPhase("error");
          setErrMsg(err?.payload?.error || "Couldn't reach the drafter — try again or write your own.");
        });
    }, 1500);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [eligible, trimmedName, trimmedTagline, type]);

  // Re-key chips on input change so each refresh animates fresh.
  const renderKey = `${type}|${trimmedName.toLowerCase()}|${trimmedTagline.toLowerCase()}`;
  const isOpen = phase !== "hidden";

  return (
    <div
      className={`suggestions-shell${isOpen ? " open" : ""}`}
      aria-live="polite"
      aria-hidden={!isOpen}
    >
      <div className="suggestions-inner">
        {phase === "thinking" && (
          <>
            <div className="suggestions-title">
              Drafting starters for {trimmedName}
              <span className="dots"><span/><span/><span/></span>
            </div>
            <div className="stack" style={{ gap: 6, marginTop: 8 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="suggestion-skel" style={{ animationDelay: `${i * 90}ms` }}>
                  <div className="suggestion-skel-line" style={{ width: "92%" }} />
                  <div className="suggestion-skel-line" style={{ width: "76%" }} />
                </div>
              ))}
            </div>
          </>
        )}

        {phase === "ready" && (
          <>
            <div className="suggestions-title">
              Try one — edit in place.
            </div>
            <div className="stack" style={{ gap: 6, marginTop: 8 }} key={renderKey}>
              {chips.map((s, i) => (
                <button
                  key={`${renderKey}-${i}`}
                  type="button"
                  onClick={() => onPick(s)}
                  className="suggestion-chip"
                  style={{ animationDelay: `${i * 110}ms` }}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        {phase === "error" && (
          <div className="suggestions-title" style={{ color: "var(--muted)" }}>
            {errMsg ?? "Couldn't draft — write something below in your own words."}
          </div>
        )}
      </div>
    </div>
  );
}
